import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import {
  Rocket, DollarSign, UserPlus, Car as CarIcon, Bell, FileText,
  Activity as ActivityIcon, Loader2,
} from "lucide-react";

const ICON_MAP = {
  rocket: Rocket,
  dollar: DollarSign,
  user_plus: UserPlus,
  car: CarIcon,
  bell: Bell,
  file_text: FileText,
};

const COLOR_MAP = {
  amber: { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-700", dot: "bg-amber-500" },
  emerald: { bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-700", dot: "bg-emerald-500" },
  blue: { bg: "bg-blue-50", border: "border-blue-300", text: "text-blue-700", dot: "bg-blue-500" },
  zinc: { bg: "bg-zinc-50", border: "border-zinc-300", text: "text-zinc-700", dot: "bg-zinc-500" },
  purple: { bg: "bg-purple-50", border: "border-purple-300", text: "text-purple-700", dot: "bg-purple-500" },
  rose: { bg: "bg-rose-50", border: "border-rose-300", text: "text-rose-700", dot: "bg-rose-500" },
  indigo: { bg: "bg-indigo-50", border: "border-indigo-300", text: "text-indigo-700", dot: "bg-indigo-500" },
};

const FILTER_TYPES = [
  { v: "rental", icon: Rocket },
  { v: "payment", icon: DollarSign },
  { v: "customer", icon: UserPlus },
  { v: "car", icon: CarIcon },
  { v: "reminder", icon: Bell },
  { v: "contract", icon: FileText },
];

const PERIODS = [
  { v: 7, label: "7d" },
  { v: 30, label: "30d" },
  { v: 90, label: "90d" },
  { v: 365, label: "1y" },
];

export default function Activity() {
  const { t, fmtCurrency } = useT();
  const [activeTypes, setActiveTypes] = useState(FILTER_TYPES.map((f) => f.v));
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/activity", {
        params: {
          limit: 150,
          types: activeTypes.join(","),
          days,
        },
      });
      setData(r.data);
    } catch (e) {
      setData({ events: [], stats: {} });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [activeTypes.join(","), days]);

  const toggleType = (v) => {
    setActiveTypes((prev) => prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]);
  };

  const groupedByDay = (data?.events || []).reduce((acc, ev) => {
    const day = (ev.at || "").slice(0, 10);
    if (!acc[day]) acc[day] = [];
    acc[day].push(ev);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="activity-page">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline">{t("activity.pageTitle")}</div>
          <h1 className="font-display font-black text-3xl tracking-tighter">{t("activity.heading")}</h1>
          <p className="text-sm text-zinc-500 mt-1">{t("activity.subtitle")}</p>
        </div>

        {/* Period selector */}
        <div className="flex gap-1">
          {PERIODS.map((p) => (
            <button
              key={p.v}
              onClick={() => setDays(p.v)}
              className={`px-3 py-1.5 text-xs font-mono border ${days === p.v ? "bg-black text-white border-black" : "border-zinc-300 hover:border-zinc-500"}`}
              data-testid={`period-${p.v}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stats summary */}
      {data?.stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-l border-t border-zinc-200">
          <StatBox label={t("activity.totalEvents")} value={data.stats.total || 0} />
          <StatBox label={t("activity.totalRevenue")} value={fmtCurrency(data.stats.total_revenue || 0)} accent />
          <StatBox label={t("activity.rentalsCreated")} value={data.stats.by_type?.rental || 0} />
          <StatBox label={t("activity.paymentsReceived")} value={data.stats.by_type?.payment || 0} />
        </div>
      )}

      {/* Filter chips */}
      <div className="flex gap-1.5 flex-wrap">
        {FILTER_TYPES.map((f) => {
          const Icon = f.icon;
          const active = activeTypes.includes(f.v);
          const count = data?.stats?.by_type?.[f.v] || 0;
          return (
            <button
              key={f.v}
              onClick={() => toggleType(f.v)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono uppercase tracking-wider border transition-colors ${
                active ? "bg-[#F5A623] text-white border-[#F5A623]" : "border-zinc-300 text-zinc-500 hover:border-zinc-500"
              }`}
              data-testid={`filter-${f.v}`}
            >
              <Icon className="w-3 h-3" /> {t(`activity.types.${f.v}`)} {count > 0 && <span className="ml-0.5 opacity-70">{count}</span>}
            </button>
          );
        })}
      </div>

      {/* Timeline */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-400">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : !data?.events?.length ? (
        <div className="border border-dashed border-zinc-300 py-20 text-center" data-testid="activity-empty">
          <ActivityIcon className="w-10 h-10 mx-auto text-zinc-300 mb-3" />
          <div className="font-display font-bold text-zinc-700">{t("activity.empty")}</div>
          <div className="text-xs text-zinc-500 font-mono mt-1">{t("activity.emptyHint")}</div>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedByDay).map(([day, items]) => (
            <div key={day}>
              <div className="overline mb-3 sticky top-0 bg-white py-1 z-10">
                {day} <span className="opacity-50">· {items.length}</span>
              </div>
              <div className="space-y-2">
                {items.map((ev, idx) => (
                  <EventRow key={`${day}-${idx}`} event={ev} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EventRow({ event }) {
  const { fmtCurrency } = useT();
  const Icon = ICON_MAP[event.icon] || ActivityIcon;
  const color = COLOR_MAP[event.color] || COLOR_MAP.zinc;
  const time = event.at?.slice(11, 16) || "";
  return (
    <div
      className={`flex items-center gap-3 border ${color.border} ${color.bg} px-4 py-3 hover:shadow-sm transition-shadow`}
      data-testid={`activity-event-${event.type}`}
    >
      <div className={`w-8 h-8 ${color.dot} text-white flex items-center justify-center shrink-0`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm truncate">{event.title}</div>
        <div className="text-[11px] font-mono text-zinc-600 truncate">{event.subtitle}</div>
      </div>
      <div className="text-right shrink-0">
        {event.amount > 0 && (
          <div className="font-mono font-bold text-sm tabular-nums">
            {fmtCurrency(event.amount)}
          </div>
        )}
        <div className="text-[10px] font-mono text-zinc-500">{time}</div>
      </div>
    </div>
  );
}

function StatBox({ label, value, accent }) {
  return (
    <div className="border-r border-b border-zinc-200 p-4">
      <div className="overline">{label}</div>
      <div className={`font-display font-black ${accent ? "text-emerald-700" : "text-zinc-900"} text-2xl tracking-tight tabular-nums mt-1`}>
        {value}
      </div>
    </div>
  );
}
